import { supabase } from '../lib/supabase';

const TASK_PHOTOS_BUCKET = 'task-photos';
const SITE_DOCUMENTS_BUCKET = 'site-documents';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'];

const isImagePath = (path) => {
  if (!path) return false;
  const ext = path?.split('.')?.pop()?.toLowerCase();
  return IMAGE_EXTENSIONS?.includes(ext);
};

/**
 * Error-surfacing pattern: see canonical doc-comment at the top of
 * src/services/clientService.js.
 */
function surfaceError(scope, error) {
  console.error(`[sitePhotoGalleryService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const sitePhotoGalleryService = {
  async getSignedUrl(bucket, filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucket)
        ?.createSignedUrl(filePath, expiresIn);
      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getSignedUrl', error) };
    }
  },

  async getPhotosBySite(siteId, filters = {}) {
    try {
      // 1. Task photos
      let taskQuery = supabase
        ?.from('task_photos')
        ?.select(`
          id,
          file_path,
          file_name,
          file_size,
          mime_type,
          caption,
          created_at,
          uploaded_by,
          task_id,
          task:daily_tasks!task_photos_task_id_fkey(id, title, category, task_date),
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId);

      if (filters?.uploadedBy && filters?.uploadedBy !== 'all') {
        taskQuery = taskQuery?.eq('uploaded_by', filters?.uploadedBy);
      }
      if (filters?.dateFrom) taskQuery = taskQuery?.gte('created_at', filters?.dateFrom);
      if (filters?.dateTo) taskQuery = taskQuery?.lte('created_at', filters?.dateTo + 'T23:59:59');
      if (filters?.taskId && filters?.taskId !== 'all') {
        taskQuery = taskQuery?.eq('task_id', filters?.taskId);
      }

      const { data: taskPhotos, error: taskErr } = await taskQuery?.order('created_at', { ascending: false });
      if (taskErr) throw taskErr;

      const taskPhotoItems = (taskPhotos || [])?.map(p => ({
        id: `task-${p?.id}`,
        rawId: p?.id,
        bucket: TASK_PHOTOS_BUCKET,
        filePath: p?.file_path,
        fileName: p?.file_name,
        fileSize: p?.file_size,
        mimeType: p?.mime_type,
        caption: p?.caption || null,
        createdAt: p?.created_at,
        uploadedBy: p?.uploaded_by,
        uploaderName: p?.uploader?.full_name || 'Unknown',
        sourceType: 'task',
        sourceLabel: 'Task',
        sourceId: p?.task_id,
        sourceName: p?.task?.title || 'Unknown Task',
        taskId: p?.task_id,
        task: p?.task,
        snagId: null,
        incidentId: null,
      }));

      // 2. Snag photos (photo_paths array)
      let snagQuery = supabase
        ?.from('snags')
        ?.select(`
          id,
          title,
          photo_paths,
          created_at,
          created_by,
          creator:user_profiles!snags_created_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId)
        ?.not('photo_paths', 'is', null)
        // NSherjFh PR 4: photo gallery hides photos from soft-deleted snags.
        ?.is('deleted_at', null);

      if (filters?.snagId && filters?.snagId !== 'all') {
        snagQuery = snagQuery?.eq('id', filters?.snagId);
      }
      if (filters?.dateFrom) snagQuery = snagQuery?.gte('created_at', filters?.dateFrom);
      if (filters?.dateTo) snagQuery = snagQuery?.lte('created_at', filters?.dateTo + 'T23:59:59');

      const { data: snags, error: snagErr } = await snagQuery?.order('created_at', { ascending: false });
      if (snagErr) console.warn('Snag photos error:', snagErr?.message);

      const snagPhotoItems = [];
      (snags || [])?.forEach(snag => {
        (snag?.photo_paths || [])?.forEach((path, idx) => {
          if (!isImagePath(path)) return;
          snagPhotoItems?.push({
            id: `snag-${snag?.id}-${idx}`,
            rawId: `${snag?.id}-${idx}`,
            bucket: SITE_DOCUMENTS_BUCKET,
            filePath: path,
            fileName: path?.split('/')?.pop() || 'snag-photo.jpg',
            fileSize: null,
            mimeType: 'image/jpeg',
            caption: null,
            createdAt: snag?.created_at,
            uploadedBy: snag?.created_by,
            uploaderName: snag?.creator?.full_name || 'Unknown',
            sourceType: 'snag',
            sourceLabel: 'Snag',
            sourceId: snag?.id,
            sourceName: snag?.title || 'Unknown Snag',
            taskId: null,
            task: null,
            snagId: snag?.id,
            incidentId: null,
          });
        });
      });

      // 3. Incident photos (file_paths array)
      let incidentQuery = supabase
        ?.from('incidents')
        ?.select(`
          id,
          incident_type,
          file_paths,
          incident_date,
          created_at,
          reported_by,
          reporter:user_profiles!incidents_reported_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId)
        ?.not('file_paths', 'is', null)
        // NSherjFh PR 5: photo gallery hides photos from soft-deleted incidents.
        ?.is('deleted_at', null);

      if (filters?.incidentId && filters?.incidentId !== 'all') {
        incidentQuery = incidentQuery?.eq('id', filters?.incidentId);
      }
      if (filters?.dateFrom) incidentQuery = incidentQuery?.gte('created_at', filters?.dateFrom);
      if (filters?.dateTo) incidentQuery = incidentQuery?.lte('created_at', filters?.dateTo + 'T23:59:59');

      const { data: incidents, error: incidentErr } = await incidentQuery?.order('created_at', { ascending: false });
      if (incidentErr) console.warn('Incident photos error:', incidentErr?.message);

      const incidentPhotoItems = [];
      (incidents || [])?.forEach(incident => {
        (incident?.file_paths || [])?.forEach((path, idx) => {
          if (!isImagePath(path)) return;
          incidentPhotoItems?.push({
            id: `incident-${incident?.id}-${idx}`,
            rawId: `${incident?.id}-${idx}`,
            bucket: SITE_DOCUMENTS_BUCKET,
            filePath: path,
            fileName: path?.split('/')?.pop() || 'incident-photo.jpg',
            fileSize: null,
            mimeType: 'image/jpeg',
            caption: null,
            createdAt: incident?.created_at || incident?.incident_date,
            uploadedBy: incident?.reported_by,
            uploaderName: incident?.reporter?.full_name || 'Unknown',
            sourceType: 'incident',
            sourceLabel: 'Incident',
            sourceId: incident?.id,
            sourceName: incident?.incident_type?.replace(/_/g, ' ') || 'Unknown Incident',
            taskId: null,
            task: null,
            snagId: null,
            incidentId: incident?.id,
          });
        });
      });

      // Merge and sort by date
      let allPhotos = [...taskPhotoItems, ...snagPhotoItems, ...incidentPhotoItems];
      allPhotos?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));

      // Apply uploadedBy filter to snag/incident photos (already applied to task photos via query)
      if (filters?.uploadedBy && filters?.uploadedBy !== 'all') {
        allPhotos = allPhotos?.filter(p => p?.uploadedBy === filters?.uploadedBy);
      }

      return { data: allPhotos, error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getPhotosBySite', error) };
    }
  },

  async getSignedUrlForPhoto(photo) {
    return sitePhotoGalleryService?.getSignedUrl(photo?.bucket, photo?.filePath, 3600);
  },
};
